import React, { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { useToast, Spinner, ErrorBanner, Badge, EmptyState } from '../../components/ui';
import { showGlobalToast } from '../../components/ui';
import AddPurchaseOrderModal from './AddPurchaseOrder';
import EditPurchaseOrderModal from './EditPurchaseOrder';
import { fmt, errMsg } from '../../utils/format';
import api, { downloadFile } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

export default function PurchaseOrders() {
  const [status, setStatus] = useState('');
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const { data, loading, error, refetch } = useFetch(`/api/purchase-orders${status?`?status=${status}`:''}`, [status]);
  const { isAdmin, isManager } = useAuth();

  const approve = async (id) => {
    try { await api.post(`/api/purchase-orders/${id}/approve`); refetch(); showGlobalToast('Purchase order approved'); } catch (err) { alert(errMsg(err)); }
  };

  const send = async (po) => {
    const msg = po.vendor_email
      ? `Send PO ${po.po_number} to ${po.vendor_email}? A PDF will be generated and emailed to the vendor and yourself.`
      : `Send PO ${po.po_number}? Note: no email address is on file for this vendor.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/api/purchase-orders/${po.id}/send`);
      refetch();
      showGlobalToast(data.message || 'PO sent');
    } catch (err) { alert(errMsg(err)); }
  };

  const resend = async (po) => {
    const msg = po.vendor_email
      ? `Resend PO ${po.po_number} to ${po.vendor_email}? This will email the PDF again.`
      : `Resend PO ${po.po_number}? Note: no email address is on file for this vendor.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/api/purchase-orders/${po.id}/resend`);
      refetch();
      showGlobalToast(data.message || 'PO resent');
    } catch (err) { alert(errMsg(err)); }
  };

  const downloadPdf = async (po) => {
    try { await downloadFile(`/api/purchase-orders/${po.id}/pdf`, `${po.po_number}.pdf`); }
    catch (err) { alert('Failed to generate PDF: ' + err.message); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this purchase order?')) return;
    try { await api.patch(`/api/purchase-orders/${id}/cancel`); refetch(); showGlobalToast('PO cancelled'); } catch (err) { alert(errMsg(err)); }
  };

  const deletePO = async (po) => {
    if (!window.confirm(`Delete ${po.po_number}? This cannot be undone.`)) return;
    try { await api.delete(`/api/purchase-orders/${po.id}`); refetch(); showGlobalToast('Purchase order deleted'); } catch (err) { alert(errMsg(err)); }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;
  return (
    <div>
      <div className="page-header">
        <h1>Purchase Orders</h1>
        <div className="page-actions">
          <select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="pending_approval">Pending approval</option>
            <option value="sent">Sent</option>
            <option value="partial">Partial</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button className="btn btn-primary btn-sm" onClick={() => setAddModal(true)}><i className="ti ti-plus" /> New PO</button>
        </div>
      </div>

      {addModal && <AddPurchaseOrderModal onClose={() => setAddModal(false)} onSaved={(msg) => { refetch(); showGlobalToast(msg || 'Saved'); }} />}
      {editModal && <EditPurchaseOrderModal po={editModal} onClose={() => setEditModal(null)} onSaved={(msg) => { refetch(); showGlobalToast(msg || 'Saved'); setEditModal(null); }} />}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>PO #</th>
                <th>Vendor</th>
                <th>Status</th>
                <th>Value</th>
                <th>Items</th>
                <th>Issued</th>
                <th>Lead time</th>
                <th>Expected arrival</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map(po => (
                <tr key={po.id}>
                  <td style={{ fontFamily:'monospace', fontSize:12 }}>{po.po_number}</td>
                  <td style={{ fontWeight:500 }}>{po.vendor_name}</td>
                  <td><Badge status={po.status} /></td>
                  <td>{fmt.currency(po.total_value)}</td>
                  <td>{po.line_items}</td>
                  <td style={{ fontSize:12 }}>{po.issued_date ? fmt.date(po.issued_date) : <span style={{ color:'var(--text-tertiary)' }}>—</span>}</td>
                  <td style={{ fontSize:12 }}>{po.lead_time_days != null ? `${po.lead_time_days}d` : <span style={{ color:'var(--text-tertiary)' }}>—</span>}</td>
                  <td style={{ fontSize:12 }}>
                    {po.expected_arrival_date
                      ? <span style={{ color: new Date(po.expected_arrival_date) < new Date() ? 'var(--color-danger)' : 'inherit' }}>{fmt.date(po.expected_arrival_date)}</span>
                      : <span style={{ color:'var(--text-tertiary)' }}>—</span>}
                  </td>
                  <td style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                    <button className="btn btn-sm" onClick={() => downloadPdf(po)} title="Download PDF"><i className="ti ti-file-type-pdf" /></button>
                    {isManager && !['received','cancelled'].includes(po.status) &&
                      <button className="btn btn-sm" onClick={() => setEditModal(po)} title="Edit"><i className="ti ti-pencil" /></button>}
                    {po.status === 'pending_approval' && isAdmin &&
                      <button className="btn btn-sm btn-primary" onClick={() => approve(po.id)}>Approve</button>}
                    {['draft','pending_approval'].includes(po.status) && isManager &&
                      <button className="btn btn-sm" onClick={() => send(po)}>Send</button>}
                    {['sent','partial','received'].includes(po.status) && isManager &&
                      <button className="btn btn-sm" onClick={() => resend(po)} title="Resend email">
                        <i className="ti ti-mail-forward" /> Resend
                      </button>}
                    {!['received','cancelled'].includes(po.status) && isManager &&
                      <button className="btn btn-sm btn-danger" onClick={() => cancel(po.id)}>Cancel</button>}
                    {po.status !== 'received' && isAdmin &&
                      <button className="btn btn-sm btn-danger" onClick={() => deletePO(po)} title="Delete"><i className="ti ti-trash" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!(data||[]).length && <EmptyState icon="ti-receipt" message="No purchase orders found" />}
        </div>
      </div>
    </div>
  );
}
